'use client'

import { PhonePrefix } from 'fukku/PhonePrefix'
import type { Phone, PhonePrefixProps } from 'fukku/PhonePrefix/PhonePrefix'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { Controller, type Validate, useFormContext } from 'react-hook-form'

type PhoneInputProps = {
	readonly name: NonNullable<PhonePrefixProps['name']>
	readonly id: PhonePrefixProps['id']
	readonly rules: {
		regex: string | RegExp
		validate?: Validate<Phone, unknown>
	}
} & Partial<PhonePrefixProps>

export function PhoneInput({
	name: controllerName,
	id,
	hintMessage,
	rules: { regex, validate: validateFn },
	...phoneInputProps
}: PhoneInputProps): React.ReactNode {
	const { t } = useLabels()
	const { control } = useFormContext()

	return (
		<Controller
			name={controllerName}
			control={control}
			rules={{
				validate: {
					required: ({ phoneNumber }) =>
						phoneNumber !== '' || t('form.empty.errormessage'),
					...(regex && {
						pattern: ({ phoneNumber }) =>
							new RegExp(regex).test(`${phoneNumber}`),
					}),
					...(validateFn && { validate: validateFn }),
				},
			}}
			render={({
				field: { name, onBlur, onChange, value },
				fieldState: { error },
			}) => (
				<PhonePrefix
					{...phoneInputProps}
					errorMessage={
						error
							? error.message || t('form.mobile.format.example.errormessage')
							: undefined
					}
					errorMessageNoPlaceholder={
						error
							? error.message || t('form.mobile.format.errormessage')
							: undefined
					}
					hintMessage={hintMessage || t('form.mobile.helpmessage')}
					id={id}
					label={t('form.mobile.label.lowercase')}
					labelFocused={t('form.mobile.label.uppercase')}
					name={name}
					onBlur={onBlur}
					onChange={onChange}
					phoneCountryIso={value.phoneCountryIso}
					phoneNumber={value.phoneNumber}
					phonePrefix={value.phonePrefix}
					prefixFieldLabel={t('form.phonecode.selector')}
				/>
			)}
		/>
	)
}
